import { sanitizeEventParams } from "./sanitizeEventParams";
import { AnalyticsEventCatalog } from "./eventCatalog";
import {
  emitAnalyticsEvent,
  emitAnalyticsUserId,
  emitAnalyticsUserProperties,
  initFirebaseAnalyticsBackend,
  isFirebaseAnalyticsReady,
} from "./firebaseAnalyticsBackend";

/**
 * Central analytics API for TheRallyAlly.
 *
 * **Usage (future instrumentation):**
 * - Import `analyticsService` from `services/analytics` in services, hooks, or bridge modules.
 * - Do **not** import `firebase/analytics` in React components.
 * - Register new events in `eventCatalog.js` and optional param types in `analytics.types.d.ts`.
 *
 * **Availability:** Production sends by default; development/test no-op unless
 * `REACT_APP_ANALYTICS_FORCE` or debug flags are set (see `analyticsEnvironment.js`).
 */
export class AnalyticsService {
  constructor() {
    /** @type {Promise<boolean> | null} */
    this._initPromise = null;
  }

  /**
   * @returns {Promise<boolean>}
   */
  initialize() {
    if (isFirebaseAnalyticsReady()) return Promise.resolve(true);
    if (!this._initPromise) {
      this._initPromise = initFirebaseAnalyticsBackend().finally(() => {
        this._initPromise = null;
      });
    }
    return this._initPromise;
  }

  isAvailable() {
    return isFirebaseAnalyticsReady();
  }

  /**
   * @param {import('./analytics.types').AnalyticsEventName} eventName
   * @param {import('./analytics.types').AnalyticsEventParams} [params]
   */
  track(eventName, params = {}) {
    const safe = sanitizeEventParams(params);
    void this.initialize().then((ready) => {
      if (!ready) return;
      emitAnalyticsEvent(eventName, safe);
    });
  }

  /**
   * @param {import('./analytics.types').AnalyticsUser | null | undefined} user
   */
  identify(user) {
    const userId =
      (typeof user?.playerId === "string" && user.playerId.trim()) ||
      (typeof user?.uid === "string" && user.uid.trim()) ||
      null;

    void this.initialize().then((ready) => {
      if (!ready) return;
      emitAnalyticsUserId(userId);
    });
  }

  /**
   * @param {import('./analytics.types').AnalyticsUserProperties} properties
   */
  setUserProperties(properties) {
    const safe = sanitizeEventParams(properties);
    void this.initialize().then((ready) => {
      if (!ready) return;
      emitAnalyticsUserProperties(safe);
    });
  }

  /**
   * @param {import('./analytics.types').AnalyticsScreenName | string} screenName
   */
  trackScreen(screenName) {
    if (!screenName || typeof screenName !== "string") return;
    const trimmed = screenName.trim().slice(0, 100);
    if (!trimmed) return;
    this.track(AnalyticsEventCatalog.SCREEN_VIEW, {
      firebase_screen: trimmed,
      firebase_screen_class: trimmed,
    });
  }
}

/** Shared singleton — prefer this export at call sites. */
export const analyticsService = new AnalyticsService();

/**
 * Convenience alias matching the task API shape (`track` vs `trackEvent`).
 * @type {Pick<AnalyticsService, 'track' | 'identify' | 'setUserProperties' | 'trackScreen' | 'initialize' | 'isAvailable'>}
 */
export const track = analyticsService.track.bind(analyticsService);
export const identify = analyticsService.identify.bind(analyticsService);
export const setUserProperties =
  analyticsService.setUserProperties.bind(analyticsService);
export const trackScreen = analyticsService.trackScreen.bind(analyticsService);
